import { useState, useEffect } from "react";
import { Stack, Typography, Divider, Skeleton, Box, Tooltip, TooltipProps, tooltipClasses, styled } from "@mui/material";
import { useSelector } from "react-redux";
import { useOpportunityCardsQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { RootState } from "src/context/store";
import { fDate } from "src/utils/format-time";
import { fNumber } from "src/utils/format-number";
import { OpportunityCard } from "src/context/api/non-branded/models";
import { useNonBrandedContext } from "./context/use-non-branded";
import FilterCursor from "src/components/filter-cursor";
import useLocales from "src/locales/use-locales";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

interface OpportunityColumnProps {
    title: string;
    color: string;
    data: OpportunityCard | undefined;
    isLoading: boolean;
    tooltipKey: string;
    opportunity: string;
}

const OpportunityColumn = ({ title, color, data, isLoading, tooltipKey, opportunity }: OpportunityColumnProps) => {
    const { setSelectedOpportunity } = useNonBrandedContext();
    const { t } = useLocales();
    const [isHovered, setIsHovered] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const searchTerms = data?.search_terms || [];
    const count = data?.count || 0;

    useEffect(() => {
        if (!isHovered) {
            document.body.style.cursor = 'default';
            return;
        }

        const handleMouseMove = (e: MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.body.style.cursor = 'none';

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.body.style.cursor = 'default';
        };
    }, [isHovered]);

    return (
        <>
            {isHovered && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: mousePosition.x,
                        top: mousePosition.y,
                        pointerEvents: 'none',
                        zIndex: 9999,
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    <FilterCursor text={t("dashboard.nonBrand.filterBy")} />
                </Box>
            )}
            <Box
                onMouseEnter={(e) => {
                    setMousePosition({ x: e.clientX, y: e.clientY });
                    setIsHovered(true);
                }}
                onClick={() => {
                    setSelectedOpportunity(opportunity);
                }}
                onMouseLeave={() => setIsHovered(false)}
                sx={{ 
                    position: 'relative', 
                    flex: 1,
                }}
            >
                <Stack className="opportunity-column" borderRadius={2} border={1} borderColor="divider" spacing={2} flex={1}>
                <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" m={0} p={0} borderBottom={1} borderColor="divider">
                        <Stack width="fit-content">
                            <LightTooltip title={t(tooltipKey)}>
                                <Typography
                                    px={2}
                                    py={1}
                                    variant="subtitle1"
                                    sx={{
                                        color: color,
                                        fontFamily: "Public Sans",
                                        fontSize: "13px",
                                        fontStyle: "normal",
                                        fontWeight: 400,
                                        cursor: 'help',
                                    }}
                                >
                                    {title}
                                </Typography>
                            </LightTooltip>
                            <Divider sx={{ borderColor: color, borderWidth: 1 }} />
                        </Stack>
                        <Typography
                                px={2}
                                py={1}
                                variant="subtitle1"
                                sx={{
                                    color: color,
                                    fontFamily: "Public Sans",
                                    fontSize: "13px",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                }}
                        >
                            {isLoading ? <Skeleton width={20} /> : count.toLocaleString()}
                        </Typography>
                    </Stack>
                </Stack>
            <Stack spacing={1.5}>
                {isLoading ? (
                    <>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Stack key={i} px={2} py={0} pb={1.5} direction="row" justifyContent="space-between" alignItems="center">
                                <Skeleton width="60%" height={20} />
                                <Skeleton width={60} height={20} />
                            </Stack>
                        ))}
                    </>
                ) : (
                    searchTerms.length === 0 ? (
                        <Stack height={226} justifyContent="center" alignItems="center">
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
                                {t("dashboard.nonBrand.noOpportunitiesDataAvailable")}
                            </Typography>
                        </Stack>
                    ) : (
                        searchTerms.map((term, index) => (
                            <>
                                <Stack
                                    key={index}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    px={2}
                                    py={0}
                                    pb={index === searchTerms.length - 1 ? 1.5 : 0}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "text.primary",
                                            fontFamily: "Public Sans",
                                            fontSize: "13px",
                                            fontStyle: "normal",
                                            fontWeight: 300,
                                        }}
                                    >
                                        {term.search_term}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#637381",
                                            fontVariantNumeric: "lining-nums tabular-nums",
                                            fontFamily: "Public Sans",
                                            fontSize: "12px",
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            lineHeight: "100%",
                                        }}
                                    >
                                        {fNumber(term.total_clicks)}
                                    </Typography>
                                </Stack>
                                {index !== searchTerms.length - 1 && (
                                    <Divider sx={{ borderColor: "divider", borderWidth: 1, borderStyle: "dashed" }} />
                                )}
                            </>
                        ))
                    )
                )}
            </Stack>
        </Stack>
        </Box>
        </>
    );
};

export default function TopOpportunities() {
    const { selectedRegion } = useNonBrandedContext();
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);
    const { t } = useLocales();

    const { data: opportunityCards, isLoading, isFetching } = useOpportunityCardsQuery(
        {
            workspace_id: workspace?.id as number,
            start_date: fDate(filterStore.start, "yyyy-MM-dd"),
            end_date: fDate(filterStore.end, "yyyy-MM-dd"),
            devices: filterStore.devices,
            countries: selectedRegion ? [selectedRegion] : [],
            platforms: filterStore.platforms,
            search_terms: filterStore.keywords,
        },
        { skip: !workspace?.id || !selectedRegion }
    );

    const overlapData = opportunityCards?.data.find((card) => card.opportunity === "overlap");
    const seoBoostData = opportunityCards?.data.find((card) => card.opportunity === "seo_boost");
    const seaBoostData = opportunityCards?.data.find((card) => card.opportunity === "sea_boost");

    return (
        <Stack spacing={2} px={0} mx={0} width="100%">
            <Stack direction="row" px={2} py={"12px"} borderBottom={1} borderColor="divider" justifyContent="space-between" alignItems="center">
                <LightTooltip title={t("dashboard.nonBrand.tooltips.opportunity")}>
                    <Typography sx={{
                        color: "#333C47",
                        fontFamily: "Public Sans",
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "100%",
                        cursor: 'help',
                    }}>
                        {t("dashboard.nonBrand.topOpportunities")}
                    </Typography>
                </LightTooltip>
                <Typography sx={{ 
                    color: "#637381",
                    textAlign: "right",
                    fontFamily: "Public Sans",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "100%",
                 }}>
                    {t("dashboard.nonBrand.sortedByClicks")}
                </Typography>
            </Stack>
            <Stack px={2} py={1} pb={2} direction="row" spacing={3}>
                <OpportunityColumn
                    title={t("dashboard.nonBrand.opportunities.overlap")}
                    color="#20D873"
                    data={overlapData}
                    isLoading={isLoading || isFetching}
                    tooltipKey="dashboard.nonBrand.tooltips.overlap"
                    opportunity="overlap"
                />
                <OpportunityColumn
                    title={t("dashboard.nonBrand.opportunities.organicBoost")}
                    color="#DBC272"
                    data={seoBoostData}
                    isLoading={isLoading || isFetching}
                    tooltipKey="dashboard.nonBrand.tooltips.organicBoost"
                    opportunity="seo_boost"
                />
                <OpportunityColumn
                    title={t("dashboard.nonBrand.opportunities.cpcBoost")}
                    color="#CEA8F0"
                    data={seaBoostData}
                    isLoading={isLoading || isFetching}
                    tooltipKey="dashboard.nonBrand.tooltips.paidBoost"
                    opportunity="sea_boost"
                />
            </Stack>
        </Stack>
    );
}
